import { useTheme } from "next-themes";
import { Sun, Moon, Monitor } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const modes = [
  { value: "light", label: "Light mode", Icon: Sun },
  { value: "system", label: "Match system", Icon: Monitor },
  { value: "dark", label: "Dark mode", Icon: Moon },
] as const;

/**
 * Three-state theme toggle: Light | System | Dark
 * Renders as a compact segmented control designed to sit inside a dropdown menu.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme();

  return (
    <div
      className={cn(
        "flex items-center justify-center gap-1 rounded-lg bg-muted/60 p-1",
        className
      )}
      role="radiogroup"
      aria-label="Theme"
    >
      {modes.map(({ value, label, Icon }) => {
        const isActive = theme === value;
        return (
          <Tooltip key={value}>
            <TooltipTrigger asChild>
              <button
                type="button"
                role="radio"
                aria-checked={isActive}
                aria-label={label}
                onClick={() => setTheme(value)}
                className={cn(
                  "relative flex h-11 w-11 items-center lg:h-8 lg:w-8 justify-center rounded-md transition-all duration-200",
                  isActive
                    ? "bg-background text-amber-600 shadow-sm dark:text-warning-700 dark:shadow-[0_0_12px_rgba(245,166,35,0.15)]"
                    : "text-muted-foreground hover:text-foreground hover:bg-background/50"
                )}
              >
                <Icon
                  size={18}
                  weight={isActive ? "duotone" : "regular"}
                  className="transition-transform duration-200"
                />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {label}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
